import React, {
    ChangeEvent,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { DataGrid, GridToolbarQuickFilter } from "../../../../libs/x-data-grid";
import { ruRU, enUS, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { IValue } from "@ui/components/Select/IValue";
import { IRights } from "./IRights";
import { Checkbox } from "@mui/material";
import { useStore } from "@ui/hooks/useStore";
import { langs } from "@lang/enums/langs";
import { IState } from "./IState";
import { observer } from "mobx-react";
import useDictionary from "@ui/hooks/useDictionary";

const fieldToParamMap = {
    settings: "change_settings",
    commands: "send_commands",
} as const;

function DeviceSelectionTable({
    values,
    onStateChange,
}: {
    values: IValue[];
    onStateChange: (newState: IState[]) => void;
}) {
    const getWord = useDictionary();
    const { lang } = useStore();

    const localeText =
        lang.lang === langs.ru
            ? ruRU.components.MuiDataGrid.defaultProps.localeText
            : enUS.components.MuiDataGrid.defaultProps.localeText;

    const [selected, setSelected] = useState<string[]>([]);
    const [rights, setRights] = useState<Record<string, IRights>>({});

    useEffect(() => {
        const resultRightsList: IState[] = selected.map((imei) => ({
            selectedImei: imei,
            rights: {
                change_settings: Boolean(rights[imei]?.change_settings),
                send_commands: Boolean(rights[imei]?.send_commands),
            },
        }));

        onStateChange(resultRightsList);
    }, [selected, rights]);

    const CheckboxCell = (params: GridRenderCellParams<any, any, any>) => {
        const param =
            fieldToParamMap[params.field as keyof typeof fieldToParamMap]; //
        const imei = params.id.toString();

        const isDisabled = !selected.includes(imei);
        const isChecked = rights[imei] ? Boolean(rights[imei][param]) : false;

        return (
            <Checkbox
                disabled={isDisabled}
                checked={isChecked}
                onChange={(_: any, checked: boolean) => {
                    const newRights = { ...rights[imei], [param]: checked };

                    setRights({ ...rights, [imei]: newRights });
                }}
            />
        );
    };

    const handleStateChange = useCallback(
        (state: any) => {
            setSelected(state.selection);
        },
        [setSelected]
    );

    const rows = useMemo(
        () =>
            values.map((row) => {
                return { id: row.value, imei: row.value, name: row.label };
            }),
        [values]
    );

    const columns: GridColDef[] = [
        { field: "imei", headerName: "IMEI", width: 150 },
        {
            field: "name",
            headerName: getWord("deviceName"),
            width: 150,
        },
        {
            field: "settings",
            headerName: getWord("changeSettingsShort"),
            headerClassName: "selection-table__check-header",
            cellClassName: "selection-table__check-cell",
            align: "center",
            width: 75,
            filterable: false,
            sortable: false,
            hideable: false,
            disableColumnMenu: true,
            renderCell: CheckboxCell,
        },
        {
            field: "commands",
            headerName: getWord("sendCommandsShort"),
            headerClassName: "selection-table__check-header",
            align: "center",
            width: 75,
            filterable: false,
            sortable: false,
            hideable: false,
            disableColumnMenu: true,
            renderCell: CheckboxCell,
        },
    ];
    return (
        <div style={{ height: 500, width: "100%" }}>
            <DataGrid
                components={{
                    Toolbar: GridToolbarQuickFilter,
                }}
                localeText={localeText}
                sx={{
                    "& .MuiTablePagination-root": {
                        paddingLeft: "0px",
                    },

                    "& .MuiDataGrid-selectedRowCount": {
                        margin: "0 8px",
                    },
                    "& .MuiInputBase-formControl": {
                        marginLeft: "0",
                        marginRight: "2px",
                    },
                    "& .MuiTablePagination-actions": {
                        marginLeft: "0px !important",
                    },
                }}
                checkboxSelection
                rows={rows}
                columns={columns}
                onStateChange={handleStateChange}
                disableSelectionOnClick
            />
        </div>
    );
}

export default memo(DeviceSelectionTable);
